import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { SuccessAlertContext } from "App";
import type { CompanySelectParams, CompanyListInfo, StoreInfo } from "../types";
import companySelectApi from "../api/companySelectApi";

/**
 * 企業リスト絞込検索hooks
 * @param initialParams イニシャルパラメート
 * @param isInitFetch 最初にフェッチするかどうか（未ログインの場合などはしない）
 */
export default function useCompanySearch(initialParams: Partial<CompanySelectParams> = {}, isInitFetch?: boolean) {
  const [companies, setCompanies] = useState<CompanyListInfo[]>([]);
  const [editedStores, setEditedStores] = useState<StoreInfo[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isMoreSearching, setIsMoreSearching] = useState<boolean>(false);
  const [init, setInit] = useState(false);
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false);
  const { showAlert } = useContext(SuccessAlertContext);

  const [params, setParams] = useState<CompanySelectParams>({
    limit: 10,
    offset: 0,
    ...initialParams,
  });
  const [error, setError] = useState<Error | null>(null);
  const fetchData = useCallback(async (prm?: CompanySelectParams) => {
    setIsSearching(true);
    setError(null);

    try {
      // API呼び出し
      const { total: newTotal, list } = await companySelectApi(prm ?? params);
      setCompanies(list);

      const allStores = list.flatMap((company) => company.stores);
      setEditedStores(allStores);
      setOffset(list.length);
      setTotal(newTotal);
    } catch (err) {
      console.error(err);
      setError(err as Error);
      showAlert("企業データの読み込みエラー", "error");
      setCompanies([]);
      setTotal(0);
    } finally {
      setIsSearching(false);
    }
  }, [params]);

  const fetchMoreData = useCallback(async (prm?: CompanySelectParams) => {
    if (!init || companies.length >= total) {
      isFastLoading.current = false;
      return;
    }

    setIsMoreSearching(true);
    setError(null);
    try {
      // API呼び出し
      const { total: newTotal, list } = await companySelectApi({...params, offset});
      setOffset(prev => prev + list.length);
      setCompanies(prev => [...prev, ...list]);

      const allStores = list.flatMap((company) => company.stores);
      setEditedStores(prev => [...prev, ...allStores]);
      setTotal(newTotal);
    } catch (err) {
      console.error(err);
      setError(err as Error);
      showAlert("企業データの読み込みエラー", "error");
      // setCompanies([]);
      // setTotal(0);
    } finally {
      isFastLoading.current = false;
      setIsMoreSearching(false);
    }
  }, [params, init, companies.length]);

  useEffect(() => {
    if (isInitFetch) {
      fetchData();
    }
  }, [fetchData, isInitFetch]);

  const refetch = (newParams?: Partial<CompanySelectParams>) => {
    if (newParams) {
      setParams((prev) => ({
        ...prev,
        ...newParams,
      }));
    } else {
      setParams((prev) => ({ ...prev }));
    }
  };

  return {
    total,
    isSearching,
    isMoreSearching,
    error,
    companies,
    editedStores,
    isFastLoading,
    setEditedStores,
    refetch,
    fetchData,
    setParams,
    setInit,
    fetchMoreData,
  };
}
